import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import FormField from '@/components/Modals/SetupWorkflow/Forms/FormField.tsx';
import Switch from '@/components/UI/FormElements/Switch';
import Banner from '@/components/UI/Banner/Banner';
import { RouteService } from '@/support/RouteService';
import { WORKFLOW_TAB_INDEX, workflowTabPath } from '@/components/Modals/SetupWorkflow/SetupWorkflow.utils';
import type { SetupWorkflowFormValues } from '@/components/Modals/SetupWorkflow/SetupWorkflow';

const consumptionPath = workflowTabPath(WORKFLOW_TAB_INDEX.CONSUMPTION);

const WorkflowConsumption: FC = () => {
  const { control } = useFormContext<SetupWorkflowFormValues>();

  return (
    <div>
      <Banner info dismiss={false}>
        <h3 className="f6 normal lh-title pb1">
          Food and fluid intake metrics can be backdated, provided no later metric exists for the same cage.
          <a
            target="_blank"
            rel="noopener noreferrer"
            className="dib link blue ml1"
            href={RouteService.help('food_fluid_tracking')}
          >
            Read more
          </a>
        </h3>
      </Banner>
      <FormField label="Enable food intake tracking" enableBorder={false}>
        <Controller
          name="workflowSetupForm.foodIntakeEnabled"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Switch value={value ?? false} onChange={onChange} testId="enable-food-intake-switch" />
          )}
        />
      </FormField>
      <FormField label="Enable fluid intake tracking">
        <Controller
          name="workflowSetupForm.fluidIntakeEnabled"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Switch value={value ?? false} onChange={onChange} testId="enable-fluid-intake-switch" />
          )}
        />
      </FormField>
      <div className="pa3 bt b--moon-gray flex justify-between items-center flex-wrap align-center">
        <div>
          <label className="mb1 f6">Set recording time</label>
          <small>Select a custom time for recording food and fluid measurements.</small>
        </div>
        <Controller
          name="workflowSetupForm.intakeTimeEnabled"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Switch value={value ?? false} onChange={onChange} testId="enable-intake-time-switch" />
          )}
        />
      </div>
      <div
        className="pa3 bt b--moon-gray flex justify-between items-center"
        data-testid="workflow-consumption-after-save-select"
      >
        <label className="mb0 f6">After saving</label>
        <Controller
          defaultValue="focus-search"
          name={`${consumptionPath}.onSave`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <select className="mw5 mb0" value={value} onChange={onChange}>
              <option value="focus-search" data-testid="after-saving-option-focus-search-bar">
                Focus search bar
              </option>
              <option value="first-subject" data-testid="after-saving-option-move-to-first-animal">
                Move to first animal
              </option>
              <option value="next-cage" data-testid="after-saving-option-move-to-next-cage">
                Move to next cage
              </option>
            </select>
          )}
        />
      </div>
    </div>
  );
};

export default WorkflowConsumption;
